/**
 * Permission groups for Patron Types configuration
 *
 * These permissions define what different patron types (Student, Faculty, Staff,
 * Community Member, etc.) can do in the system. They control self-service features
 * available to patrons through their dashboard and OPAC.
 *
 * Note: These are separate from Staff permissions (permission-catalog.ts).
 * Patron permissions govern what library users (patrons) can do themselves,
 * while staff permissions govern what library employees can do.
 */

export interface PermissionGroup {
  label: string;
  permissions: {
    key: string;
    label: string;
    description?: string;
  }[];
}

export const PATRON_TYPE_PERMISSION_GROUPS: PermissionGroup[] = [
  {
    label: "Dashboard",
    permissions: [
      {
        key: "dashboard.view",
        label: "View Dashboard",
        description: "Access the patron dashboard with account overview",
      },
      {
        key: "dashboard.viewStats",
        label: "View Reading Stats",
        description: "View personal reading statistics and history summaries",
      },
    ],
  },
  {
    label: "Catalog",
    permissions: [
      {
        key: "catalog.view",
        label: "View Catalog",
        description: "View and search catalog items",
      },
      {
        key: "catalog.list",
        label: "Browse Catalog",
        description: "Browse catalog listings and collections",
      },
      {
        key: "catalog.requestHold",
        label: "Request Hold",
        description: "Place holds on items that are currently checked out",
      },
      {
        key: "catalog.reserve",
        label: "Reserve Items",
        description: "Reserve available catalog items for pickup",
      },
      {
        key: "catalog.writeReview",
        label: "Write Reviews",
        description: "Write reviews and ratings for catalog items",
      },
      {
        key: "catalog.createList",
        label: "Create Reading Lists",
        description: "Create and share personal reading lists",
      },
    ],
  },
  {
    label: "Circulation",
    permissions: [
      {
        key: "circulation.borrow",
        label: "Borrow Items",
        description: "Borrow books and other items from the library",
      },
      {
        key: "circulation.renew",
        label: "Renew Items",
        description: "Renew borrowed items online",
      },
      {
        key: "circulation.viewHistory",
        label: "View Borrowing History",
        description: "View personal borrowing and return history",
      },
    ],
  },
  {
    label: "Digital Resources",
    permissions: [
      {
        key: "digital.view",
        label: "View Digital Resources",
        description: "Access and browse digital resources",
      },
      {
        key: "digital.download",
        label: "Download Digital Resources",
        description: "Download e-books, articles, and digital content",
      },
      {
        key: "digital.stream",
        label: "Stream Media",
        description: "Stream audio/video content from the library",
      },
    ],
  },
  {
    label: "Inter-Library Loan",
    permissions: [
      {
        key: "ill.requestBorrow",
        label: "Request ILL",
        description: "Request items from other libraries via Inter-Library Loan",
      },
      {
        key: "ill.view",
        label: "View ILL Status",
        description: "View the status of inter-library loan requests",
      },
    ],
  },
  {
    label: "Course Reserves",
    permissions: [
      {
        key: "courseReserves.view",
        label: "View Course Reserves",
        description: "View items placed on course reserves by faculty",
      },
      {
        key: "courseReserves.create",
        label: "Create Reserve Lists",
        description: "Create course reserve lists (typically for faculty patrons)",
      },
    ],
  },
  {
    label: "Events & Programs",
    permissions: [
      {
        key: "events.view",
        label: "View Events",
        description: "View library events, workshops, and programs",
      },
      {
        key: "events.register",
        label: "Register for Events",
        description: "Register/RSVP for library events and programs",
      },
    ],
  },
  {
    label: "Fines & Fees",
    permissions: [
      {
        key: "fines.viewOwn",
        label: "View My Fines",
        description: "View personal fine balance and payment history",
      },
      {
        key: "fines.payOnline",
        label: "Pay Fines Online",
        description: "Make online payments for fines and fees",
      },
    ],
  },
  {
    label: "Account Management",
    permissions: [
      {
        key: "account.viewHistory",
        label: "View Account History",
        description: "View borrowing and account activity history",
      },
      {
        key: "account.updateProfile",
        label: "Update Profile",
        description: "Update personal profile information and preferences",
      },
      {
        key: "account.changePassword",
        label: "Change Password",
        description: "Change account password",
      },
      {
        key: "account.manageNotificationPrefs",
        label: "Manage Notification Preferences",
        description: "Configure email, SMS, and push notification preferences",
      },
    ],
  },
  {
    label: "Notifications",
    permissions: [
      {
        key: "notifications.view",
        label: "View Notifications",
        description: "View notifications (due dates, holds, events, etc.)",
      },
      {
        key: "notifications.email",
        label: "Email Notifications",
        description: "Receive email notifications for account activity",
      },
      {
        key: "notifications.sms",
        label: "SMS Notifications",
        description: "Receive SMS notifications for due dates and holds",
      },
    ],
  },
  {
    label: "Acquisitions",
    permissions: [
      {
        key: "acquisitions.suggest",
        label: "Suggest Purchases",
        description: "Suggest items for the library to purchase",
      },
    ],
  },
];

/**
 * Get all permission keys from all groups
 */
export function getAllPatronPermissionKeys(): string[] {
  const allKeys: string[] = [];
  PATRON_TYPE_PERMISSION_GROUPS.forEach((group) => {
    group.permissions.forEach((perm) => {
      allKeys.push(perm.key);
    });
  });
  return allKeys;
}

/**
 * Default patron type permission presets
 * These serve as templates when creating new patron types
 */
export const PATRON_PERMISSION_PRESETS: Record<string, string[]> = {
  STUDENT: [
    "dashboard.view",
    "dashboard.viewStats",
    "catalog.view",
    "catalog.list",
    "catalog.requestHold",
    "catalog.reserve",
    "catalog.writeReview",
    "catalog.createList",
    "circulation.borrow",
    "circulation.renew",
    "circulation.viewHistory",
    "digital.view",
    "digital.download",
    "ill.requestBorrow",
    "ill.view",
    "courseReserves.view",
    "events.view",
    "events.register",
    "fines.viewOwn",
    "fines.payOnline",
    "account.viewHistory",
    "account.updateProfile",
    "account.changePassword",
    "account.manageNotificationPrefs",
    "notifications.view",
    "notifications.email",
    "acquisitions.suggest",
  ],

  FACULTY: [
    "dashboard.view",
    "dashboard.viewStats",
    "catalog.view",
    "catalog.list",
    "catalog.requestHold",
    "catalog.reserve",
    "catalog.writeReview",
    "catalog.createList",
    "circulation.borrow",
    "circulation.renew",
    "circulation.viewHistory",
    "digital.view",
    "digital.download",
    "digital.stream",
    "ill.requestBorrow",
    "ill.view",
    "courseReserves.view",
    "courseReserves.create",
    "events.view",
    "events.register",
    "fines.viewOwn",
    "fines.payOnline",
    "account.viewHistory",
    "account.updateProfile",
    "account.changePassword",
    "account.manageNotificationPrefs",
    "notifications.view",
    "notifications.email",
    "notifications.sms",
    "acquisitions.suggest",
  ],

  COMMUNITY_MEMBER: [
    "dashboard.view",
    "catalog.view",
    "catalog.list",
    "catalog.requestHold",
    "catalog.reserve",
    "circulation.borrow",
    "circulation.renew",
    "circulation.viewHistory",
    "events.view",
    "events.register",
    "fines.viewOwn",
    "fines.payOnline",
    "account.viewHistory",
    "account.updateProfile",
    "account.changePassword",
    "notifications.view",
    "notifications.email",
  ],

  RESTRICTED: [
    "dashboard.view",
    "catalog.view",
    "catalog.list",
    "circulation.viewHistory",
    "fines.viewOwn",
    "account.viewHistory",
    "account.updateProfile",
    "notifications.view",
  ],
};
